import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import { Moon, Brain, Calendar, HelpCircle, AlertTriangle } from "lucide-react";

export default function LackOfSleepMentalHealthPage() {
  const publishedDate = "2024-03-15";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/03/lack-of-sleep-mental-health.jpg";

  const faqs = [
    {
      q: "Does lack of sleep really affect 78% of adults' mental health?",
      a: "Surveys show that approximately 78% of adults report that lack of sleep negatively impacts their mental health, increasing symptoms of anxiety, depression, irritability, and emotional dysregulation.",
    },
    {
      q: "How does sleep deprivation affect the brain?",
      a: "Sleep deprivation impairs the prefrontal cortex (executive function and emotional regulation) while hyperactivating the amygdala (fear and emotional reactivity), creating a brain state vulnerable to anxiety and depression.",
    },
    {
      q: "Can treating sleep improve depression and anxiety?",
      a: "Yes. Restorative sleep is foundational to mental health. Addressing sleep through CBT-I, medication management, or treating underlying conditions like sleep apnea can significantly reduce depression and anxiety symptoms.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Lack of Sleep Is Compromising the Mental Health of 78% of Adults | Neuro Wellness"
        description="Discover how sleep deprivation impacts 78% of adults' mental health—brain changes, anxiety and depression links, and evidence-based solutions for restorative sleep."
        canonical="/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults"
        keywords="lack of sleep mental health, sleep deprivation anxiety depression, sleep and brain health, insomnia treatment Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults#webpage",
                name: "Lack of Sleep Is Compromising the Mental Health of 78% of Adults",
                url: "https://neurowellnesstms.com/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Analysis of how sleep deprivation compromises mental health in 78% of adults, including neurobiological effects and treatment pathways.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Moon className="h-4 w-4" />
                    Sleep & Mental Health
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Mar 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  Lack of Sleep Is Compromising the{" "}
                  <span className="text-accent">
                    Mental Health of 78% of Adults
                  </span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  How sleep deprivation rewires the brain, fuels anxiety and
                  depression, and what evidence-based care can do to restore
                  restorative rest.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Sleep Assessment
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Get Sleep & Mental Health Support
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "Lack of Sleep & Mental Health",
                  href: "/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                A staggering <strong>78% of adults</strong> report that lack of
                sleep compromises their mental health, according to national
                sleep surveys. The relationship between sleep and psychological
                wellbeing is not merely correlational—sleep deprivation directly
                alters brain function in ways that promote anxiety, depression,
                and emotional instability.
              </p>

              <h2>The Sleep-Mental Health Connection</h2>
              <p>
                Sleep is not passive rest—it is an active neurological process
                essential for memory consolidation, emotional processing, and
                neurotransmitter balance. When sleep is chronically disrupted,
                the consequences cascade across every mental health domain.
              </p>

              <h2>How Sleep Deprivation Rewires the Brain</h2>
              <p>
                Brain imaging studies reveal that sleep deprivation{" "}
                <strong>impairs the prefrontal cortex</strong>—the brain region
                responsible for executive function, impulse control, and
                emotional regulation—while{" "}
                <strong>hyperactivating the amygdala</strong>, the brain's fear
                and emotional reactivity center. This creates a brain state
                vulnerable to anxiety and depression.
              </p>

              <h2>The Vicious Cycle: Sleep & Mental Illness</h2>
              <p>
                Sleep problems and mental health conditions create a
                bidirectional loop. Depression and anxiety cause insomnia, and
                insomnia worsens depression and anxiety. Breaking this cycle
                requires treating both the sleep disorder and the underlying
                psychiatric condition simultaneously.
              </p>

              <h2>Evidence-Based Solutions</h2>
              <ul>
                <li>
                  <strong>
                    CBT-I (Cognitive Behavioral Therapy for Insomnia):
                  </strong>{" "}
                  The gold-standard, non-pharmacological treatment for chronic
                  insomnia.
                </li>
                <li>
                  <strong>Medication Management:</strong> Carefully prescribed
                  sleep aids or mood-stabilizing medications that address both
                  sleep and psychiatric symptoms.
                </li>
                <li>
                  <strong>Treating Underlying Conditions:</strong> Addressing
                  depression, anxiety, or sleep apnea that may be driving sleep
                  disruption through{" "}
                  <Link
                    to="/what-is-tms-therapy"
                    className="text-primary hover:underline font-semibold"
                  >
                    TMS
                  </Link>{" "}
                  or therapy.
                </li>
              </ul>
            </article>

            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Mental Health Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/how-to-get-mental-health-help"
                    className="hover:underline"
                  >
                    → How to Get Mental Health Help
                  </Link>
                </li>
                <li>
                  <Link
                    to="/what-is-the-difference-between-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Anxiety vs Depression
                  </Link>
                </li>
                <li>
                  <Link
                    to="/the-benefits-of-psychotherapy-for-managing-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Benefits of Psychotherapy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-to-see-a-psychiatrist"
                    className="hover:underline"
                  >
                    → How to See a Psychiatrist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/learn/always-tired-depression-fatigue"
                    className="hover:underline"
                  >
                    → Why Am I So Tired All the Time? Depression Fatigue
                  </Link>
                </li>
                <li>
                  <Link
                    to="/learn/why-is-my-anxiety-worse-in-the-morning"
                    className="hover:underline"
                  >
                    → Why Is My Anxiety Worse in the Morning?
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
